if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      // === BONUS CONFIG (sync with snippets/cart-bonus-progress-bar.liquid) ===
      static BONUS_VARIANT_ID = 47354961887490;
      static BONUS_THRESHOLD_KOP = 50000; // 500 UAH in kopiykas
      static BONUS_PROP_KEY = "_bonus_gift";

      constructor() {
        super();

        this.form = this.querySelector("form");
        this.formInput = this.form?.querySelector("[name=id]");
        if (this.formInput) this.formInput.disabled = false;
        this.form?.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart = document.querySelector("cart-drawer");
        this.cartType = this.cart?.dataset.cartType || "page";

        this.submitButton = this.querySelector('[type="submit"]');
        this.body = document.querySelector("body");

        if (document.querySelector("cart-drawer"))
          this.submitButton?.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";

        this.handleErrorMessage = this.handleErrorMessage.bind(this);
        this.redirectAfterSubmit = this.redirectAfterSubmit.bind(this);
        this.disableLoadingInButton = this.disableLoadingInButton.bind(this);
      }

      closeComplementaryProduct() {
        const complementaryOverlayPage = document.querySelector(
          ".wt__quick-buy--page-overlay",
        );
        if (this.body.classList.contains("quick-buy-page-overlay"))
          this.body.classList.remove("quick-buy-page-overlay");
        if (
          complementaryOverlayPage.classList.contains(
            "wt__quick-buy--page-overlay--open",
          )
        )
          complementaryOverlayPage.classList.remove(
            "wt__quick-buy--page-overlay--open",
          );
      }

      // === BONUS HELPERS ===
      _getVariantPrice(variantId) {
        try {
          const scripts = document.querySelectorAll(
            'variant-options script[type="application/json"]',
          );
          for (const s of scripts) {
            const variants = JSON.parse(s.textContent);
            if (Array.isArray(variants)) {
              const found = variants.find((v) => v.id === variantId);
              if (found && typeof found.price === "number") return found.price;
            }
          }
        } catch (e) {}
        // Fallback: read displayed price from DOM
        try {
          const priceEl = document.querySelector(
            ".wt-product__price [data-price], .wt-product__price .price-item--sale, .wt-product__price .price-item--regular",
          );
          if (priceEl) {
            const txt = priceEl.textContent.replace(/[^\d.,]/g, "").replace(",", ".");
            const num = parseFloat(txt);
            if (!isNaN(num)) return Math.round(num * 100);
          }
        } catch (e) {}
        return null;
      }

      async _shouldAddBonus(mainVariantId, mainQty) {
        const BONUS_ID = ProductForm.BONUS_VARIANT_ID;
        const THRESHOLD = ProductForm.BONUS_THRESHOLD_KOP;
        const PROP = ProductForm.BONUS_PROP_KEY;

        // Never add bonus item if we're adding the bonus item itself
        if (mainVariantId === BONUS_ID) return false;

        // Use synchronous cache if available (injected by Liquid on page load
        // + refreshed in background after each cart op). Falls back to fetch.
        let cart = window.__cartCache;
        if (!cart) {
          try {
            cart = await fetch("/cart.js", { credentials: "same-origin" }).then(
              (r) => r.json(),
            );
          } catch (e) {
            return false;
          }
        }

        // Already in cart?
        const hasBonus = (cart.items || []).some(
          (it) =>
            it.variant_id === BONUS_ID &&
            it.properties &&
            it.properties[PROP] === "true",
        );
        if (hasBonus) return false;

        // Eligible total excluding any existing bonus entry
        let currentTotal = 0;
        for (const it of cart.items || []) {
          if (
            it.variant_id === BONUS_ID &&
            it.properties &&
            it.properties[PROP] === "true"
          )
            continue;
          currentTotal += it.final_line_price || 0;
        }

        const variantPrice = this._getVariantPrice(mainVariantId);
        if (variantPrice == null) return false; // Can't reliably predict — fall back to async progress-bar logic

        const newTotal = currentTotal + variantPrice * mainQty;
        return newTotal >= THRESHOLD;
      }

      _buildItemsFromForm(formData) {
        const variantId = parseInt(formData.get("id"), 10);
        const qty = parseInt(formData.get("quantity") || "1", 10) || 1;

        const item = { id: variantId, quantity: qty };

        // Collect properties[...] from form
        const properties = {};
        for (const [key, value] of formData.entries()) {
          if (key.startsWith("properties[") && key.endsWith("]")) {
            const propName = key.slice(11, -1);
            properties[propName] = value;
          }
        }
        if (Object.keys(properties).length) item.properties = properties;

        // selling_plan
        const sellingPlan = formData.get("selling_plan");
        if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);

        return { item, variantId, qty };
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();
        this.cart?.setActiveElement(document.activeElement);

        const loader = this.querySelector(".loading-overlay__spinner");

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        if (loader) loader.classList.remove("hidden");

        const formData = new FormData(this.form);
        const { item: mainItem, variantId: mainVariantId, qty: mainQty } =
          this._buildItemsFromForm(formData);

        const items = [mainItem];

        // Decide if bonus should be added in the SAME request
        let bonusAddedNow = false;
        try {
          if (await this._shouldAddBonus(mainVariantId, mainQty)) {
            items.push({
              id: ProductForm.BONUS_VARIANT_ID,
              quantity: 1,
              properties: { [ProductForm.BONUS_PROP_KEY]: "true" },
            });
            bonusAddedNow = true;
          }
        } catch (e) {
          console.warn("[ProductForm] bonus pre-check failed", e);
        }

        const sectionIds =
          this.cart && typeof this.cart.getSectionsToRender === "function"
            ? this.cart.getSectionsToRender().map((s) => s.id)
            : [];

        const body = {
          items,
          sections: sectionIds.join(","),
          sections_url: window.location.pathname,
        };

        fetch(`${routes.cart_add_url}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/javascript",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: mainVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.error = true;

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");

              this.dispatchEvent(
                new CustomEvent("cart-drawer:refresh", {
                  detail: { response: response },
                  bubbles: true,
                  composed: true,
                }),
              );

              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // Mark bonus state so cart-bonus-progress-bar doesn't try to re-add
            if (bonusAddedNow) {
              try {
                localStorage.setItem("bonus_pb_gift", "1");
              } catch (e) {}
            }

            // Refresh cart cache in background for next interaction
            fetch("/cart.js", { credentials: "same-origin" })
              .then((r) => r.json())
              .then((c) => { window.__cartCache = c; })
              .catch(() => {});

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: mainVariantId,
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.redirectAfterSubmit(response);
                  });
                },
                { once: true },
              );
              if (this.cartType === "drawer") quickAddModal.hide(true);
            } else {
              const isClosedCart = !document.body.classList.contains(
                "page-overlay-cart-on",
              );
              this.redirectAfterSubmit(response, isClosedCart);
            }
          })
          .catch((e) => {
            console.error(e);
            if (e instanceof TypeError && e.message.includes("'cart-drawer'")) {
              location.reload();
            }
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            this.submitButton.removeAttribute("aria-disabled");
            if (this.error) {
              this.disableLoadingInButton();
              return;
            }
            this.quick_add_container = document.querySelector(
              ".wt__quick-buy__container",
            );
            this.quick_add_overlay = document.querySelector(
              ".wt__quick-buy--page-overlay",
            );
            this.quick_add_product =
              this.quick_add_container?.querySelector(".wt-product");
            this.loader = this.quick_add_container?.querySelector(
              ".wt__quick-buy-loader",
            );

            if (
              this.quick_add_container &&
              !this.quick_add_container.classList.contains("hidden")
            ) {
              this.quick_add_product?.remove();
              this.quick_add_container.classList.remove(
                "wt__quick-buy__container--open",
              );
              this.quick_add_overlay?.classList.remove(
                "wt__quick-buy--page-overlay--open",
              );
              document.body.classList.remove("quick-buy-page-overlay");
              this.loader?.classList.remove("hidden");
            }

            setTimeout(() => {
              this.disableLoadingInButton();
            }, 200);
          });
      }

      disableLoadingInButton() {
        this.submitButton.classList.remove("loading");
        this.querySelector(".loading-overlay__spinner")?.classList.add(
          "hidden",
        );
      }

      redirectAfterSubmit(response, isClosedCart = true) {
        const body = document.body;
        const isCartPage = body.classList.contains("template-cart");

        if (this.cartType === "drawer" && isCartPage) {
          const overlay = document.querySelector(
            ".wt__quick-buy--page-overlay",
          );
          overlay?.classList.remove("wt__quick-buy--page-overlay--open");
          body.classList.remove("quick-buy-page-overlay");
        } else if (this.cartType === "drawer") {
          this.cart.renderContents(response, isClosedCart);
          return;
        }
        window.location = window.routes.cart_url;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
